import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils";
import { toast } from "sonner";
import {
  Plus, Trash2, GripVertical, Save, RotateCcw, History,
  Loader2, X, ChevronDown, ChevronUp, Eye, FileText,
} from "lucide-react";

/**
 * iter176 — ProposalContentEditor
 *
 * Reusable editor for the two content-heavy sections of the Public
 * Proposal page: Deliverables (grouped) + Terms & Conditions.
 *
 * Modes:
 *   - `global`   → operates on `/api/proposal-content/defaults`.
 *   - `proposal` → operates on `/api/proposal-content/proposals/{bookingId}`
 *                  with the current global defaults as the fallback.
 *
 * Design notes:
 *   - HTML5 drag-and-drop for reorder (no external library).
 *   - Preview panel reuses the SAME bullet styling as the Public
 *     Proposal page so the admin sees the true output.
 *   - Version safety: Save / Cancel / Reset / Restore Version.
 */
export default function ProposalContentEditor({
  mode = "global",
  bookingId = null,
  onClose = null,
  showPreview = true,
}) {
  const isProposal = mode === "proposal";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState({}); // {version, updated_at, updated_by, can_edit, customized}
  const [sections, setSections] = useState([]);
  const [terms, setTerms] = useState([]);
  const [initial, setInitial] = useState({ sections: [], terms: [] });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [showPreviewPanel, setShowPreviewPanel] = useState(showPreview);

  const canEdit = !!meta?.can_edit;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (isProposal) {
        const [p, g] = await Promise.all([
          api.get(`/proposal-content/proposals/${bookingId}`),
          api.get(`/proposal-content/defaults`),
        ]);
        const startSections = p.data.sections_custom
          ?? p.data.sections_snapshot
          ?? g.data.sections
          ?? [];
        const startTerms = p.data.terms_custom
          ?? p.data.terms_snapshot
          ?? g.data.terms
          ?? [];
        setSections(cloneSections(startSections));
        setTerms([...startTerms]);
        setInitial({ sections: cloneSections(startSections), terms: [...startTerms] });
        setMeta({
          can_edit: p.data.can_edit,
          customized: p.data.customized,
          snapshot_version: p.data.snapshot_version,
          has_proposal: p.data.has_proposal,
        });
      } else {
        const { data } = await api.get(`/proposal-content/defaults`);
        setSections(cloneSections(data.sections));
        setTerms([...(data.terms || [])]);
        setInitial({
          sections: cloneSections(data.sections),
          terms: [...(data.terms || [])],
        });
        setMeta({
          version: data.version,
          updated_at: data.updated_at,
          updated_by: data.updated_by,
          history_count: data.history_count,
          can_edit: data.can_edit,
        });
      }
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e?.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }, [isProposal, bookingId]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(
    () => !isEqualContent({ sections, terms }, initial),
    [sections, terms, initial]
  );

  // --- section ops -------------------------------------------------------
  const updateSection = (idx, patch) => {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const addSection = () => {
    setSections((prev) => [
      ...prev,
      { id: `new_${Date.now()}`, title: "New Section", bullets: ["New item"] },
    ]);
  };
  const deleteSection = (idx) => {
    if (!window.confirm(`Delete section "${sections[idx]?.title}"?`)) return;
    setSections((prev) => prev.filter((_, i) => i !== idx));
  };
  const moveSection = (from, to) => {
    if (to < 0 || to >= sections.length || from === to) return;
    setSections((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };
  const addBullet = (sIdx) => {
    setSections((prev) => prev.map((s, i) =>
      i === sIdx ? { ...s, bullets: [...s.bullets, "New bullet"] } : s
    ));
  };
  const updateBullet = (sIdx, bIdx, val) => {
    setSections((prev) => prev.map((s, i) =>
      i === sIdx
        ? { ...s, bullets: s.bullets.map((b, j) => (j === bIdx ? val : b)) }
        : s
    ));
  };
  const deleteBullet = (sIdx, bIdx) => {
    setSections((prev) => prev.map((s, i) =>
      i === sIdx ? { ...s, bullets: s.bullets.filter((_, j) => j !== bIdx) } : s
    ));
  };
  const moveBullet = (sIdx, from, to) => {
    setSections((prev) => prev.map((s, i) => {
      if (i !== sIdx) return s;
      if (to < 0 || to >= s.bullets.length || from === to) return s;
      const bullets = [...s.bullets];
      const [item] = bullets.splice(from, 1);
      bullets.splice(to, 0, item);
      return { ...s, bullets };
    }));
  };

  // --- terms ops ---------------------------------------------------------
  const addTerm = () => setTerms((prev) => [...prev, "New term"]);
  const updateTerm = (i, v) => setTerms((prev) => prev.map((t, j) => (j === i ? v : t)));
  const deleteTerm = (i) => setTerms((prev) => prev.filter((_, j) => j !== i));
  const moveTerm = (from, to) => {
    if (to < 0 || to >= terms.length || from === to) return;
    setTerms((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  // --- save/cancel/reset -------------------------------------------------
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const cleanSections = sections
        .map((s) => ({
          ...s,
          title: (s.title || "").trim(),
          bullets: (s.bullets || []).map((b) => String(b).trim()).filter(Boolean),
        }))
        .filter((s) => s.title);
      const cleanTerms = terms.map((t) => String(t).trim()).filter(Boolean);
      if (isProposal) {
        await api.patch(`/proposal-content/proposals/${bookingId}`, {
          sections: cleanSections,
          terms: cleanTerms,
        });
        toast.success("Proposal customized");
      } else {
        await api.put(`/proposal-content/defaults`, {
          sections: cleanSections,
          terms: cleanTerms,
        });
        toast.success("Global defaults saved · applies to NEW proposals only");
      }
      await load();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setSections(cloneSections(initial.sections));
    setTerms([...initial.terms]);
    setError("");
  };

  const resetToFactory = async () => {
    if (!window.confirm(
      isProposal
        ? "Reset THIS proposal to its saved snapshot (removes your customization)?"
        : "Reset the global defaults to the factory ship-time content?"
    )) return;
    setSaving(true);
    setError("");
    try {
      if (isProposal) {
        await api.delete(`/proposal-content/proposals/${bookingId}`);
        toast.success("Proposal customization cleared");
      } else {
        await api.post(`/proposal-content/defaults/reset`);
        toast.success("Reset to factory defaults");
      }
      await load();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e?.message || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/proposal-content/defaults/history`);
      setHistory(data.history || []);
      setHistoryOpen(true);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e?.message || "History load failed");
    }
  };
  const restoreVersion = async (v) => {
    if (!window.confirm(`Restore version ${v}? Current state will be added to history.`)) return;
    try {
      await api.post(`/proposal-content/defaults/restore/${v}`);
      toast.success(`Restored version ${v}`);
      setHistoryOpen(false);
      await load();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e?.message || "Restore failed");
    }
  };

  // -----------------------------------------------------------------------
  if (loading) {
    return (
      <div className="py-12 text-center text-stone-500" data-testid="pce-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
        Loading editor…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="proposal-content-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-stone-500">
        <div className="flex items-center gap-2 flex-wrap">
          {isProposal ? (
            <>
              <span className="editorial-eyebrow">Customize for this Proposal</span>
              {meta?.customized && (
                <span className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] uppercase tracking-widest">
                  customized
                </span>
              )}
            </>
          ) : (
            <>
              <span className="editorial-eyebrow">Global Defaults</span>
              <span>v{meta.version}</span>
              {meta.updated_at && (
                <span>· updated {new Date(meta.updated_at).toLocaleString("en-IN")}</span>
              )}
              {meta.updated_by && meta.updated_by !== "system" && (
                <span>by {meta.updated_by}</span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => setShowPreviewPanel((v) => !v)}
            data-testid="pce-toggle-preview"
          >
            <Eye className="w-3.5 h-3.5" /> {showPreviewPanel ? "Hide" : "Show"} Preview
          </button>
          {!isProposal && meta.history_count > 0 && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={loadHistory}
              data-testid="pce-history-btn"
            >
              <History className="w-3.5 h-3.5" /> Restore Version
            </button>
          )}
        </div>
      </div>

      {!isProposal && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900" data-testid="pce-global-note">
          Changes to Global Defaults apply only to <strong>NEW</strong> proposals created after saving. Existing proposals keep their original content.
        </div>
      )}

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="pce-error">
          {error}
        </div>
      )}

      <div className={showPreviewPanel ? "grid grid-cols-1 lg:grid-cols-2 gap-5" : ""}>
        {/* ================================ EDITOR PANEL ================================ */}
        <div className="space-y-5">
          {/* Sections */}
          <section className="space-y-2" data-testid="pce-sections">
            <div className="flex items-center justify-between">
              <div className="font-heading font-semibold text-stone-900 text-base">Deliverable Sections</div>
              {canEdit && (
                <button type="button" className="btn btn-outline btn-sm" onClick={addSection} data-testid="pce-add-section">
                  <Plus className="w-3.5 h-3.5" /> Add Section
                </button>
              )}
            </div>
            <div className="space-y-3">
              {sections.map((s, i) => (
                <SectionCard
                  key={s.id || i}
                  index={i}
                  total={sections.length}
                  section={s}
                  canEdit={canEdit}
                  onChange={(patch) => updateSection(i, patch)}
                  onDelete={() => deleteSection(i)}
                  onMove={(dir) => moveSection(i, i + dir)}
                  onAddBullet={() => addBullet(i)}
                  onUpdateBullet={(bIdx, val) => updateBullet(i, bIdx, val)}
                  onDeleteBullet={(bIdx) => deleteBullet(i, bIdx)}
                  onMoveBullet={(from, dir) => moveBullet(i, from, from + dir)}
                  onDropSection={(from) => moveSection(from, i)}
                />
              ))}
              {sections.length === 0 && (
                <div className="card p-6 text-center text-stone-500 text-sm">
                  No sections. Click "Add Section" to create one.
                </div>
              )}
            </div>
          </section>

          {/* Terms */}
          <section className="space-y-2" data-testid="pce-terms">
            <div className="flex items-center justify-between">
              <div className="font-heading font-semibold text-stone-900 text-base">Terms &amp; Conditions</div>
              {canEdit && (
                <button type="button" className="btn btn-outline btn-sm" onClick={addTerm} data-testid="pce-add-term">
                  <Plus className="w-3.5 h-3.5" /> Add Term
                </button>
              )}
            </div>
            <div className="card p-4">
              {terms.length === 0 && (
                <div className="text-center text-stone-500 text-sm py-3">No terms. Add one above.</div>
              )}
              <ul className="space-y-1.5">
                {terms.map((t, i) => (
                  <TermRow
                    key={i}
                    index={i}
                    total={terms.length}
                    value={t}
                    canEdit={canEdit}
                    onChange={(v) => updateTerm(i, v)}
                    onDelete={() => deleteTerm(i)}
                    onMove={(dir) => moveTerm(i, i + dir)}
                    onDropTerm={(from) => moveTerm(from, i)}
                  />
                ))}
              </ul>
            </div>
          </section>
        </div>

        {/* ================================ PREVIEW PANEL ================================ */}
        {showPreviewPanel && (
          <div className="space-y-4">
            <div className="editorial-eyebrow flex items-center gap-1.5">
              <FileText className="w-3 h-3" /> Preview
            </div>
            <ProposalPreview sections={sections} terms={terms} />
          </div>
        )}
      </div>

      {/* Action row */}
      {canEdit && (
        <div className="sticky bottom-0 bg-white border-t border-stone-200 -mx-6 px-6 py-3 flex flex-wrap items-center justify-end gap-2" data-testid="pce-actions">
          {onClose && (
            <button type="button" className="btn btn-outline" onClick={onClose} data-testid="pce-close">
              Close
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline"
            onClick={resetToFactory}
            disabled={saving}
            data-testid="pce-reset"
          >
            <RotateCcw className="w-4 h-4" />
            {isProposal ? "Reset to Snapshot" : "Reset to Default"}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            onClick={cancel}
            disabled={!dirty || saving}
            data-testid="pce-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="pce-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      )}

      {/* History drawer */}
      {historyOpen && (
        <HistoryDrawer
          items={history}
          onClose={() => setHistoryOpen(false)}
          onRestore={restoreVersion}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionCard — one deliverable section with bullet list.
// ---------------------------------------------------------------------------
function SectionCard({
  index, total, section, canEdit,
  onChange, onDelete, onMove,
  onAddBullet, onUpdateBullet, onDeleteBullet, onMoveBullet,
  onDropSection,
}) {
  const handleDragStart = (e) => {
    e.dataTransfer.setData("text/pce-section", String(index));
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDrop = (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("text/pce-section");
    if (raw === "") return;
    const from = parseInt(raw, 10);
    if (Number.isFinite(from)) onDropSection(from);
  };
  return (
    <div
      className="card p-4 space-y-3"
      data-testid={`pce-section-${index}`}
      onDragOver={(e) => canEdit && e.preventDefault()}
      onDrop={canEdit ? handleDrop : undefined}
    >
      <div className="flex items-center gap-2">
        {canEdit && (
          <span
            draggable
            onDragStart={handleDragStart}
            className="cursor-grab text-stone-400 hover:text-stone-700"
            title="Drag to reorder"
            data-testid={`pce-section-drag-${index}`}
          >
            <GripVertical className="w-4 h-4" />
          </span>
        )}
        <span className="text-[10px] tracking-[0.4em] uppercase text-stone-500 font-medium">
          {String(index + 1).padStart(2, "0")}
        </span>
        <input
          className="input flex-1 font-semibold"
          value={section.title}
          onChange={(e) => onChange({ title: e.target.value })}
          disabled={!canEdit}
          placeholder="Section title"
          data-testid={`pce-section-title-${index}`}
        />
        {canEdit && (
          <div className="flex items-center gap-1">
            <button type="button" className="p-1 rounded hover:bg-stone-100 disabled:opacity-40"
              onClick={() => onMove(-1)} disabled={index === 0}
              title="Move up" data-testid={`pce-section-up-${index}`}>
              <ChevronUp className="w-4 h-4" />
            </button>
            <button type="button" className="p-1 rounded hover:bg-stone-100 disabled:opacity-40"
              onClick={() => onMove(1)} disabled={index === total - 1}
              title="Move down" data-testid={`pce-section-down-${index}`}>
              <ChevronDown className="w-4 h-4" />
            </button>
            <button type="button" className="p-1 rounded hover:bg-red-50 text-red-700"
              onClick={onDelete} title="Delete section"
              data-testid={`pce-section-delete-${index}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <ul className="space-y-1.5 pl-6">
        {section.bullets.map((b, j) => (
          <BulletRow
            key={j}
            sectionIndex={index}
            bulletIndex={j}
            total={section.bullets.length}
            value={b}
            canEdit={canEdit}
            onChange={(v) => onUpdateBullet(j, v)}
            onDelete={() => onDeleteBullet(j)}
            onMove={(dir) => onMoveBullet(j, dir)}
          />
        ))}
      </ul>
      {canEdit && (
        <button
          type="button"
          className="btn btn-outline btn-sm ml-6"
          onClick={onAddBullet}
          data-testid={`pce-section-add-bullet-${index}`}
        >
          <Plus className="w-3 h-3" /> Add bullet
        </button>
      )}
    </div>
  );
}

function BulletRow({ sectionIndex, bulletIndex, total, value, canEdit, onChange, onDelete, onMove }) {
  return (
    <li className="flex items-center gap-2" data-testid={`pce-bullet-${sectionIndex}-${bulletIndex}`}>
      <span className="w-1 h-1 rounded-full bg-stone-400 shrink-0" />
      <input
        className="input flex-1 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!canEdit}
        placeholder="Bullet text"
        data-testid={`pce-bullet-input-${sectionIndex}-${bulletIndex}`}
      />
      {canEdit && (
        <div className="flex items-center gap-1">
          <button type="button" className="p-1 rounded hover:bg-stone-100 disabled:opacity-40"
            onClick={() => onMove(-1)} disabled={bulletIndex === 0} title="Move up"
            data-testid={`pce-bullet-up-${sectionIndex}-${bulletIndex}`}>
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button type="button" className="p-1 rounded hover:bg-stone-100 disabled:opacity-40"
            onClick={() => onMove(1)} disabled={bulletIndex === total - 1} title="Move down"
            data-testid={`pce-bullet-down-${sectionIndex}-${bulletIndex}`}>
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <button type="button" className="p-1 rounded hover:bg-red-50 text-red-700"
            onClick={onDelete} title="Delete bullet"
            data-testid={`pce-bullet-delete-${sectionIndex}-${bulletIndex}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </li>
  );
}

function TermRow({ index, total, value, canEdit, onChange, onDelete, onMove, onDropTerm }) {
  const handleDragStart = (e) => {
    e.dataTransfer.setData("text/pce-term", String(index));
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDrop = (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("text/pce-term");
    if (raw === "") return;
    const from = parseInt(raw, 10);
    if (Number.isFinite(from)) onDropTerm(from);
  };
  return (
    <li
      className="flex items-center gap-2"
      data-testid={`pce-term-${index}`}
      onDragOver={(e) => canEdit && e.preventDefault()}
      onDrop={canEdit ? handleDrop : undefined}
    >
      {canEdit && (
        <span
          draggable
          onDragStart={handleDragStart}
          className="cursor-grab text-stone-400 hover:text-stone-700"
          title="Drag to reorder"
          data-testid={`pce-term-drag-${index}`}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </span>
      )}
      <span className="w-1 h-1 rounded-full bg-stone-400 shrink-0" />
      <input
        className="input flex-1 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!canEdit}
        placeholder="Term text"
        data-testid={`pce-term-input-${index}`}
      />
      {canEdit && (
        <div className="flex items-center gap-1">
          <button type="button" className="p-1 rounded hover:bg-stone-100 disabled:opacity-40"
            onClick={() => onMove(-1)} disabled={index === 0} title="Move up"
            data-testid={`pce-term-up-${index}`}>
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button type="button" className="p-1 rounded hover:bg-stone-100 disabled:opacity-40"
            onClick={() => onMove(1)} disabled={index === total - 1} title="Move down"
            data-testid={`pce-term-down-${index}`}>
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <button type="button" className="p-1 rounded hover:bg-red-50 text-red-700"
            onClick={onDelete} title="Delete term"
            data-testid={`pce-term-delete-${index}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// ProposalPreview — mirrors the exact typography/styles used on the
// Public Proposal page so the admin sees an accurate preview.
// ---------------------------------------------------------------------------
function ProposalPreview({ sections, terms }) {
  return (
    <div
      className="rounded-2xl border border-[#e7d5b4]/60 bg-[#fdf7ef] px-4 sm:px-6 py-5 sm:py-6 space-y-5"
      data-testid="pce-preview"
    >
      {/* Deliverables preview */}
      <div>
        <div className="text-center mb-4">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#8c7a63] font-medium">
            Deliverables
          </div>
          <div className="mt-1 border-t border-[#e7d5b4]/70 w-16 mx-auto" />
        </div>
        <div className="space-y-3">
          {sections.length === 0 && (
            <div className="text-sm text-stone-500 text-center py-3">
              No deliverables yet. Add sections to preview them here.
            </div>
          )}
          {sections.map((g, i) => (
            <div
              key={g.id || i}
              className="bg-white rounded-2xl border border-[#e7d5b4]/60 px-4 sm:px-6 py-4"
            >
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-[10px] tracking-[0.4em] uppercase text-[#8c7a63] font-medium">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div
                  className="font-serif text-lg text-[#9c7550]"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {g.title || "(untitled)"}
                </div>
              </div>
              <ul className="space-y-1.5 pl-1">
                {g.bullets.map((b, j) => (
                  <li key={j} className="flex items-start gap-3 text-[#3b2f24] text-[13px] leading-relaxed">
                    <span className="mt-2 w-1 h-1 rounded-full bg-[#b18860] shrink-0" />
                    <span>{b || <em className="text-stone-400">(empty)</em>}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Terms preview */}
      <div>
        <div className="text-center mb-3">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#8c7a63] font-medium">
            Terms &amp; Conditions
          </div>
          <div className="mt-1 border-t border-[#e7d5b4]/70 w-16 mx-auto" />
        </div>
        <div className="bg-white rounded-2xl border border-[#e7d5b4]/60 px-4 sm:px-6 py-4">
          {terms.length === 0 && (
            <div className="text-sm text-stone-500 text-center py-2">
              No terms yet.
            </div>
          )}
          <ul className="space-y-2">
            {terms.map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-[#3b2f24] text-[13px] leading-relaxed">
                <span className="mt-2 w-1 h-1 rounded-full bg-[#b18860] shrink-0" />
                <span>{t || <em className="text-stone-400">(empty)</em>}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function HistoryDrawer({ items, onClose, onRestore }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-xl shadow-xl overflow-hidden max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="pce-history-drawer"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
          <div className="font-heading font-semibold">Previous Versions</div>
          <button type="button" className="p-1 rounded hover:bg-stone-100" onClick={onClose}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {items.length === 0 && (
            <div className="text-sm text-stone-500 text-center py-6">No previous versions.</div>
          )}
          {items.map((h) => (
            <div key={h.version} className="border border-stone-200 rounded-md p-3 flex items-start justify-between gap-3" data-testid={`pce-history-v${h.version}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm">Version {h.version}</div>
                <div className="text-xs text-stone-500">
                  {h.updated_at ? new Date(h.updated_at).toLocaleString("en-IN") : "—"}
                  {h.updated_by && h.updated_by !== "system" && ` · ${h.updated_by}`}
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  {(h.sections?.length || 0)} sections · {(h.terms?.length || 0)} terms
                </div>
              </div>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => onRestore(h.version)} data-testid={`pce-restore-v${h.version}`}>
                <RotateCcw className="w-3.5 h-3.5" /> Restore
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
function cloneSections(list) {
  return (list || []).map((s) => ({
    id: s.id || `s_${Math.random().toString(36).slice(2, 10)}`,
    title: s.title || "",
    bullets: [...(s.bullets || [])],
  }));
}

function isEqualContent(a, b) {
  if ((a.terms || []).length !== (b.terms || []).length) return false;
  for (let i = 0; i < a.terms.length; i++) if (a.terms[i] !== b.terms[i]) return false;
  if ((a.sections || []).length !== (b.sections || []).length) return false;
  for (let i = 0; i < a.sections.length; i++) {
    const s1 = a.sections[i]; const s2 = b.sections[i];
    if ((s1.title || "") !== (s2.title || "")) return false;
    if ((s1.bullets || []).length !== (s2.bullets || []).length) return false;
    for (let j = 0; j < s1.bullets.length; j++) {
      if (s1.bullets[j] !== s2.bullets[j]) return false;
    }
  }
  return true;
}
